import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  SignOut, ClipboardText, Buildings, Package, ShieldCheck, FileText,
  Storefront, UsersThree, GearSix, ArrowUpRight, ArrowRight,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { HaleWordmark } from '../../components/HaleBrand';
import HaleHead from '../../components/hale/HaleHead';
import { T, FONT } from './_theme';

const API = process.env.REACT_APP_BACKEND_URL;

/* Hale Console — the Hale marketplace operating console for Hale staff
 * (role hale_admin) and Agyekum/ToxIntel admins. Hale-branded (white /
 * Chivo / red), distinct from the brand workspace (/hale/dashboard) and the
 * ToxIntel assessor center. Single operations surface: review applications,
 * manage brands, manage products, manage Hale Reviewed decisions, and watch
 * marketplace activity. */

const NAV = [
  { label: 'Dashboard',          to: '/hale/console' },
  { label: 'Applications',       to: '/hale/console#applications' },
  { label: 'Brands',             to: '/hale/console#brands' },
  { label: 'Products',           to: '/admin/hale-reviewed' },
  { label: 'Hale Reviewed Queue', to: '/admin/hale-reviewed' },
  { label: 'Documents',          to: '/hale/console#documents' },
  { label: 'Marketplace',        to: '/hale/console#marketplace' },
  { label: 'Partner Network',    to: '/hale/console#partners' },
  { label: 'Settings',           to: '/hale/console#settings' },
];

const PRODUCT_STATUS_LABEL = {
  submitted: 'Submitted', under_review: 'Under review',
  information_requested: 'Info requested', approved: 'Approved',
  conditional_approval: 'Conditional', not_approved: 'Not approved',
};

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="rounded-xl border p-5" style={{ borderColor: T.LINE, backgroundColor: T.WHITE }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>{label}</p>
      <p className="mt-2 text-3xl font-black tabular-nums" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{value}</p>
      {hint && <p className="mt-1 text-[12px]" style={{ color: T.MUTED }}>{hint}</p>}
    </div>
  );
}

function SectionCard({ id, icon: Icon, title, desc, to, cta, children }) {
  return (
    <section id={id} className="rounded-xl border p-6 scroll-mt-24" style={{ borderColor: T.LINE, backgroundColor: T.WHITE }} data-testid={`hale-console-section-${id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(230,57,70,0.10)' }}>
            <Icon size={18} weight="bold" style={{ color: T.RED }} />
          </span>
          <div>
            <h3 className="text-[16px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{title}</h3>
            {desc && <p className="mt-1 text-[13px] max-w-xl" style={{ color: T.MUTED }}>{desc}</p>}
          </div>
        </div>
        {to && (
          <Link to={to} data-testid={`hale-console-cta-${id}`}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-[12px] font-semibold"
            style={{ backgroundColor: T.RED, color: T.WHITE }}>
            {cta || 'Open'} <ArrowUpRight size={14} weight="bold" />
          </Link>
        )}
      </div>
      {children && <div className="mt-5">{children}</div>}
    </section>
  );
}

export default function HaleConsolePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState(null);
  const [apps, setApps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const opts = { withCredentials: true };
      const safe = (p) => p.then(r => r.data).catch(() => null);
      const [s, ps, ap] = await Promise.all([
        safe(axios.get(`${API}/api/hale-marketplace/public/stats`, opts)),
        safe(axios.get(`${API}/api/hale-reviewed/admin/stats`, opts)),
        safe(axios.get(`${API}/api/hale-marketplace/admin/applications?limit=6`, opts)),
      ]);
      if (!active) return;
      setStats(s); setProducts(ps); setApps(ap); setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/hale/login', { replace: true });
  };

  const productTotal = products?.total ?? 0;
  const productQueue = products ? (products.submitted + products.under_review + products.information_requested) : 0;
  const productApproved = products ? (products.approved + products.conditional_approval) : 0;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: T.NEUTRAL, color: T.INK, fontFamily: FONT.BODY }}>
      <HaleHead title="Hale Console" />

      {/* Top bar */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${T.LINE}` }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-4">
          <Link to="/hale/console" className="flex items-center gap-2.5 flex-shrink-0" data-testid="hale-console-brand">
            <HaleWordmark size="sm" />
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded" style={{ color: T.WHITE, backgroundColor: T.INK, fontFamily: FONT.MONO }}>Console</span>
          </Link>

          <nav className="flex-1 flex items-center gap-0.5 overflow-x-auto no-scrollbar" data-testid="hale-console-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                end={n.to === '/hale/console'}
                data-testid={`hale-console-nav-${n.label.toLowerCase().split(' ')[0]}`}
                className="px-2.5 py-2 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors"
                style={({ isActive }) => ({
                  color: isActive && n.to === '/hale/console' ? T.INK : T.MUTED,
                  backgroundColor: isActive && n.to === '/hale/console' ? 'rgba(230,57,70,0.08)' : 'transparent',
                })}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <button onClick={handleLogout} data-testid="hale-console-logout"
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-semibold border"
            style={{ borderColor: T.LINE, color: T.INK, backgroundColor: T.WHITE }}>
            <SignOut size={14} weight="bold" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 py-9">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: T.RED, fontFamily: FONT.MONO }}>Hale Operating Console</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-[-0.02em]" style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid="hale-console-title">
              Marketplace operations.
            </h1>
            <p className="mt-2 text-[14px]" style={{ color: T.MUTED }}>
              Welcome, {user?.name || 'Hale staff'} · {user?.email}. Review applications, manage brands and products,
              run Hale Reviewed decisions, and watch marketplace activity.
            </p>
          </div>
          <Link to="/admin/hale-reviewed" data-testid="hale-console-primary-cta"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-lg text-[13px] font-semibold"
            style={{ backgroundColor: T.RED, color: T.WHITE }}>
            Open Hale Reviewed Queue <ArrowRight size={15} weight="bold" />
          </Link>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="hale-console-stats">
          <StatCard label="Applications" value={loading ? '—' : (stats?.total_applications ?? 0)} hint={`${stats?.in_review ?? 0} in review`} testid="stat-applications" />
          <StatCard label="Approved brands" value={loading ? '—' : (stats?.approved_brands ?? 0)} hint="Across the League" testid="stat-brands" />
          <StatCard label="Hale Reviewed queue" value={loading ? '—' : productQueue} hint={`${productTotal} products total`} testid="stat-queue" />
          <StatCard label="Approved products" value={loading ? '—' : productApproved} hint="Reviewed™ eligible" testid="stat-approved-products" />
        </div>

        {/* Sections */}
        <div className="mt-8 grid lg:grid-cols-2 gap-5">
          <SectionCard id="applications" icon={ClipboardText} title="Applications"
            desc="Brand intake awaiting eligibility review and approval / revision decisions."
            to="/hale/console#applications" cta="Review">
            {loading ? (
              <p className="text-[13px]" style={{ color: T.MUTED }}>Loading…</p>
            ) : apps && apps.items && apps.items.length > 0 ? (
              <ul className="divide-y" style={{ borderColor: T.LINE }} data-testid="hale-console-applications-list">
                {apps.items.map((a) => (
                  <li key={a.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold truncate" style={{ color: T.INK }}>
                        {(a.draft?.brand?.brand_name) || a.email_masked || 'Brand application'}
                      </p>
                      <p className="text-[11px]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>{a.email_masked}</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded whitespace-nowrap"
                      style={{ color: T.MUTED, backgroundColor: T.NEUTRAL, fontFamily: FONT.MONO }}>
                      {String(a.status || 'submitted').replace(/_/g, ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px]" style={{ color: T.MUTED }}>No applications yet. New brand intakes will appear here.</p>
            )}
          </SectionCard>

          <SectionCard id="products" icon={Package} title="Products & Hale Reviewed decisions"
            desc="The live product review queue: INCI, claims, documents, status history, and review decisions."
            to="/admin/hale-reviewed" cta="Manage">
            {loading ? (
              <p className="text-[13px]" style={{ color: T.MUTED }}>Loading…</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="hale-console-product-breakdown">
                {products && Object.keys(PRODUCT_STATUS_LABEL).map((k) => (
                  <span key={k} className="text-[11px] px-2.5 py-1.5 rounded-md border" style={{ borderColor: T.LINE, color: T.INK }}>
                    {PRODUCT_STATUS_LABEL[k]} · <span className="font-bold tabular-nums">{products[k] ?? 0}</span>
                  </span>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard id="brands" icon={Buildings} title="Brands"
            desc="All Hale-onboarded brands — tiering, contact metadata, and pilots." />

          <SectionCard id="marketplace" icon={Storefront} title="Marketplace activity"
            desc="Live marketplace listings and member activity across the League."
            to="/hale/marketplace" cta="View" />

          <SectionCard id="documents" icon={FileText} title="Documents"
            desc="Documentation requests and supporting files (COA, SDS, stability, claims substantiation)." />

          <SectionCard id="partners" icon={UsersThree} title="Partner Network"
            desc="Formulation, testing, packaging, and fulfillment partners with preferred League rates." />

          <SectionCard id="settings" icon={GearSix} title="Settings"
            desc="Console preferences. Reviewer of record: regulatory@tagc.us." />

          <SectionCard id="verified" icon={ShieldCheck} title="Reviewed™ / Verified™"
            desc="Badge issuance follows an approved Hale Reviewed decision and (where applicable) Verified™ documentation review." />
        </div>
      </main>
    </div>
  );
}
